import { useState, type ComponentProps, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useResizeObserverRef } from "@wojtekmaj/react-hooks";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { Promisable } from "type-fest";

import {
  findInstrumentDisplayName,
  type FinishedAssessment,
  type GuestAsk,
} from "@thalamos/haven-common";

import { createRoute } from "../../routing/createRoute.js";
import {
  ACTION_TABLE_MIN_WIDTH,
  ActionTable,
} from "../ActionTable/ActionTable.js";
import { AssessmentKindChip } from "../AssessmentKindChip/AssessmentKindChip.js";
import { GuestAssessor, GuestChip } from "../GuestAssessor/GuestAssessor.js";
import { ShareWithGuestDialog } from "../ShareWithGuestDialog/ShareWithGuestDialog.js";
import { useToast } from "../ToastProvider/ToastProvider.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/** Why a finished assessment offers this session nothing. */
export type FinishedNoActionReason = "contents_withheld";

/**
 * What this session may do with one finished assessment: read it, read only,
 * or nothing, and why.
 */
export type FinishedAssessmentActions =
  { view: true } | { view: false; why: FinishedNoActionReason };

/** One finished assessment, as this session sees it. */
export type FinishedAssessmentRow = {
  assessment: FinishedAssessment;
  /** Undefined where the screen cannot tell; see `assessorNameOf`. */
  assessorName: string | undefined;
  /** Who approved it; undefined where the screen cannot tell, or none did. */
  approverName: string | undefined;
  /** Who asked for it, where a guest owns it; see `guestAskOf`. */
  guestAsk?: GuestAsk | undefined;
  /** How many guests can read it now, through a share. */
  guestsSharedWith: number;
  actions: FinishedAssessmentActions;
};

/** Sharing finished assessments with somebody outside the service, where this session may. */
export type GuestSharing = Pick<
  ComponentProps<typeof ShareWithGuestDialog>,
  "personName" | "onMatch" | "onShare"
>;

const COLUMNS = [
  "kind",
  "instrument",
  "assessor",
  "approvedBy",
  "finished",
] as const;

/** What each column of a row shows, and the assessor's name for its actions, shared by the table and its stacked form. */
const useCells = ({
  assessment,
  assessorName,
  approverName,
  guestAsk,
  guestsSharedWith,
}: FinishedAssessmentRow) => {
  const { t } = useTranslation();
  const name = assessorName ?? t("personPage.assessments.unknownAssessor");

  return {
    /** The assessor as the row's actions name them. */
    assessorName: name,
    kind: assessment.definitive ? (
      <AssessmentKindChip definitive />
    ) : (
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        <AssessmentKindChip definitive={false} />
        {guestAsk === undefined ? null : <GuestChip />}
      </Stack>
    ),
    instrument: findInstrumentDisplayName(assessment.instrument.name) ?? null,
    assessor:
      guestAsk === undefined ? (
        name
      ) : (
        <GuestAssessor name={name} guestAsk={guestAsk} withGuest={false} />
      ),
    approvedBy:
      assessment.status === "approved"
        ? (approverName ?? t("personPage.assessments.history.unknownApprover"))
        : t("personPage.assessments.history.noApprovalNeeded"),
    finished: (
      <>
        {t(`personPage.assessments.history.finished.${assessment.status}`, {
          date: CALENDAR_DATE.format(assessment.finishedAt),
        })}
        {guestsSharedWith === 0 ? null : (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {t("personPage.assessments.history.sharedWith", {
              count: guestsSharedWith,
            })}
          </Typography>
        )}
      </>
    ),
  } satisfies Record<(typeof COLUMNS)[number] | "assessorName", ReactNode>;
};

const RowActions = ({
  row: { assessment, actions },
  assessor,
}: {
  row: FinishedAssessmentRow;
  /** The assessor as the row names them. */
  assessor: string;
}) => {
  const { t } = useTranslation();

  if (!actions.view) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t(`personPage.assessments.history.why.${actions.why}`)}
      </Typography>
    );
  }

  const of = { assessor, date: CALENDAR_DATE.format(assessment.finishedAt) };

  return (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button
        variant="outlined"
        size="small"
        component={RouterLink}
        to={createRoute.assessmentSummary(assessment.id)}
        aria-label={t(
          `personPage.assessments.history.summaryOf.${assessment.status}`,
          of,
        )}
      >
        {t("personPage.assessments.history.summary")}
      </Button>
      <Button
        variant="outlined"
        size="small"
        component={RouterLink}
        to={createRoute.assessment(assessment.id)}
        aria-label={t(
          `personPage.assessments.history.viewOf.${assessment.status}`,
          of,
        )}
      >
        {t("personPage.assessments.history.view")}
      </Button>
    </Stack>
  );
};

const TableLayoutRow = ({ row }: { row: FinishedAssessmentRow }) => {
  const cells = useCells(row);

  return (
    <TableRow>
      {COLUMNS.map((column) => (
        <TableCell key={column}>{cells[column]}</TableCell>
      ))}
      <TableCell align="right">
        <RowActions row={row} assessor={cells.assessorName} />
      </TableCell>
    </TableRow>
  );
};

const StackedRow = ({ row }: { row: FinishedAssessmentRow }) => {
  const { t } = useTranslation();
  const cells = useCells(row);

  return (
    <Stack spacing={1} sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {cells.kind}
        <Typography variant="subtitle2">{cells.finished}</Typography>
      </Stack>
      {(["instrument", "assessor", "approvedBy"] as const).map((column) =>
        cells[column] === null ? null : (
          <Typography key={column} variant="body2">
            {t(`personPage.assessments.history.column.${column}`)}:{" "}
            {cells[column]}
          </Typography>
        ),
      )}
      <Box>
        <RowActions row={row} assessor={cells.assessorName} />
      </Box>
    </Stack>
  );
};

/** How much of the history `rows` is, and how to ask for more of it. */
export type HistoryPaging = {
  /** How many finished assessments there are in all. */
  total: number;
  /** Whether a read is in flight, of a larger page or the same one again. */
  loading: boolean;
  /** Whether the last read failed, leaving `rows` as they were. */
  failed: boolean;
  /** Absent once no larger page can be asked for. */
  onShowMore?: (() => Promisable<void>) | undefined;
};

/** Says how much of the history is shown, and offers older ones while it can. */
const MoreOfTheHistory = ({
  shown,
  paging: { total, loading, failed, onShowMore },
}: {
  shown: number;
  paging: HistoryPaging;
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
    >
      <Typography variant="caption" color="text.secondary">
        {t(
          onShowMore === undefined
            ? "personPage.assessments.history.showingMostRecent"
            : "personPage.assessments.history.showing",
          { shown, total },
        )}
      </Typography>

      {onShowMore === undefined ? null : (
        <Button
          size="small"
          disabled={loading}
          onClick={() => void onShowMore()}
        >
          {t("personPage.assessments.history.showOlder")}
        </Button>
      )}

      {failed ? (
        <Typography variant="caption" color="error">
          {t("personPage.assessments.history.showOlderFailed")}
        </Typography>
      ) : null}
    </Stack>
  );
};

/**
 * A young person's finished assessments, each opening read only where this
 * session may read it, and saying why not where it may not.
 */
export const AssessmentHistory = ({
  rows,
  failed,
  paging,
  sharing,
}: {
  /** In the order to show them; undefined until read, which is not the same as none. */
  rows: FinishedAssessmentRow[] | undefined;
  /** Whether they could not be worked out, so will not arrive by waiting. */
  failed: boolean;
  /** Absent where `rows` is the whole history. */
  paging?: HistoryPaging | undefined;
  /** Absent where the session may not share assessments with a guest here. */
  sharing?: GuestSharing | undefined;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [sharingOpen, setSharingOpen] = useState(false);
  const [ref, entry] = useResizeObserverRef<HTMLDivElement>();
  // Judged by the component's own width rather than the viewport's, so it also
  // stacks when laid out in a narrow container. Unmeasured reads as wide.
  const width = entry?.contentRect.width;
  const stacked = width !== undefined && width < ACTION_TABLE_MIN_WIDTH;

  const content = (): ReactNode => {
    if (rows === undefined) {
      return failed ? (
        <Alert severity="error">
          {t("personPage.assessments.history.loadFailed")}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("common.loading")}
        </Typography>
      );
    }

    if (rows.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("personPage.assessments.history.empty")}
        </Typography>
      );
    }

    if (stacked) {
      return (
        <Stack divider={<Divider />}>
          {rows.map((row) => (
            <StackedRow key={row.assessment.id} row={row} />
          ))}
        </Stack>
      );
    }

    return (
      <ActionTable
        columns={COLUMNS.map((column) =>
          t(`personPage.assessments.history.column.${column}`),
        )}
        actionsLabel={t("personPage.assessments.column.actions")}
      >
        {rows.map((row) => (
          <TableLayoutRow key={row.assessment.id} row={row} />
        ))}
      </ActionTable>
    );
  };

  return (
    <Paper
      ref={ref}
      component="section"
      aria-labelledby="assessment-history-title"
      variant="outlined"
      sx={{ p: { xs: 2, sm: 3 } }}
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography variant="h6" component="h2" id="assessment-history-title">
            {t("personPage.assessments.history.title")}
          </Typography>
          {sharing === undefined || rows === undefined ? null : (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSharingOpen(true);
              }}
            >
              {t("shareWithGuest.open")}
            </Button>
          )}
        </Stack>
        {content()}
        {rows !== undefined &&
        paging !== undefined &&
        paging.total > rows.length ? (
          <MoreOfTheHistory shown={rows.length} paging={paging} />
        ) : null}
      </Stack>

      {sharing === undefined || !sharingOpen || rows === undefined ? null : (
        // Mounted afresh on each opening, so nothing chosen and no warning
        // ticked carries over from the last share.
        <ShareWithGuestDialog
          open
          personName={sharing.personName}
          assessments={rows.flatMap((row) =>
            row.actions.view
              ? [
                  {
                    assessment: row.assessment,
                    assessorName:
                      row.assessorName ??
                      t("personPage.assessments.unknownAssessor"),
                  },
                ]
              : [],
          )}
          onMatch={sharing.onMatch}
          onShare={async (request) => {
            const outcome = await sharing.onShare(request);

            if (outcome.kind === "shared") {
              setSharingOpen(false);
              toast.show(
                // Names the address the sharer gave, not the guest: an earlier
                // guest they confirmed may go by another name.
                t("shareWithGuest.shared", {
                  count: request.assessmentIds.length,
                  email: request.email,
                }),
              );
            }

            return outcome;
          }}
          onClose={() => {
            setSharingOpen(false);
          }}
        />
      )}
    </Paper>
  );
};
